import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { X, ChevronRight, ChevronLeft, Check, SkipForward } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  title: { en: "Quick survey", ru: "Небольшой опрос", he: "סקר קצר" },
  subtitle: { en: "Help us understand you better — takes 30 seconds", ru: "Помогите нам понять вас лучше — займёт 30 секунд", he: "עזור לנו להכיר אותך טוב יותר — לוקח 30 שניות" },
  step_of: { en: "Step", ru: "Шаг", he: "שלב" },
  of: { en: "of", ru: "из", he: "מתוך" },
  next: { en: "Next", ru: "Далее", he: "הבא" },
  back: { en: "Back", ru: "Назад", he: "חזרה" },
  finish: { en: "Finish", ru: "Завершить", he: "סיים" },
  skip_survey: { en: "Skip survey", ru: "Пропустить опрос", he: "דלג על הסקר" },
  skip_q: { en: "Skip", ru: "Пропустить", he: "דלג" },
  thanks: { en: "Thank you!", ru: "Спасибо!", he: "תודה!" },
  thanks_sub: { en: "Your answers help us improve iFollowers for everyone.", ru: "Ваши ответы помогают нам улучшать iFollowers.", he: "התשובות שלך עוזרות לנו לשפר את iFollowers." },

  // Q1
  q1_title: { en: "How did you hear about us?", ru: "Как вы узнали о нас?", he: "איך שמעת עלינו?" },
  q1_google: { en: "Google / Search", ru: "Google / Поиск", he: "גוגל / חיפוש" },
  q1_instagram: { en: "Instagram", ru: "Instagram", he: "אינסטגרם" },
  q1_tiktok: { en: "TikTok", ru: "TikTok", he: "טיקטוק" },
  q1_youtube: { en: "YouTube", ru: "YouTube", he: "יוטיוב" },
  q1_friend: { en: "Friend / word of mouth", ru: "Друг / сарафанное радио", he: "חבר / המלצה" },
  q1_telegram: { en: "Telegram", ru: "Telegram", he: "טלגרם" },
  q1_other: { en: "Other", ru: "Другое", he: "אחר" },

  // Q2
  q2_title: { en: "Were you invited by someone?", ru: "Вас пригласил кто-то?", he: "מישהו הזמין אותך?" },
  q2_placeholder: { en: "Referral code or name (optional)", ru: "Реферальный код или имя (необязательно)", he: "קוד הפניה או שם (אופציונלי)" },
  q2_no: { en: "No, found it myself", ru: "Нет, нашёл сам", he: "לא, מצאתי לבד" },

  // Q3
  q3_title: { en: "Which platforms do you use?", ru: "Какими платформами вы пользуетесь?", he: "אילו פלטפורמות אתה משתמש?" },
  q3_sub: { en: "Select all that apply", ru: "Выберите все подходящие", he: "בחר את כל המתאימים" },

  // Q4
  q4_title: { en: "How old are you?", ru: "Сколько вам лет?", he: "בן כמה אתה?" },
  q4_u18: { en: "Under 18", ru: "До 18", he: "מתחת ל-18" },
  q4_18_24: { en: "18–24", ru: "18–24", he: "18–24" },
  q4_25_34: { en: "25–34", ru: "25–34", he: "25–34" },
  q4_35_44: { en: "35–44", ru: "35–44", he: "35–44" },
  q4_45p: { en: "45+", ru: "45+", he: "45+" },

  // Q5
  q5_title: { en: "What brings you to iFollowers?", ru: "Зачем вы пришли на iFollowers?", he: "מה מביא אותך ל-iFollowers?" },
  q5_grow: { en: "Grow my social media", ru: "Расти в соцсетях", he: "לגדול ברשתות חברתיות" },
  q5_earn: { en: "Earn iPoints / tasks", ru: "Зарабатывать iPoints / задания", he: "להרוויח iPoints / משימות" },
  q5_f4f: { en: "Follow4Follow exchange", ru: "Follow4Follow обмен", he: "חילופי Follow4Follow" },
  q5_business: { en: "Business / promotion", ru: "Бизнес / продвижение", he: "עסק / קידום" },
  q5_curious: { en: "Just curious", ru: "Просто интересно", he: "סתם סקרן" },

  // Q6
  q6_title: { en: "Anything else you'd like to tell us?", ru: "Есть что-то, что хотите нам сказать?", he: "יש משהו שתרצה לספר לנו?" },
  q6_placeholder: { en: "Your thoughts, suggestions or feedback… (optional)", ru: "Ваши мысли, предложения или отзыв… (необязательно)", he: "מחשבות, הצעות או פידבק… (אופציונלי)" },
};

const L = (key, lang) => T[key]?.[lang] || T[key]?.en || key;

const PLATFORMS = ["Instagram", "TikTok", "YouTube", "Facebook", "Twitter/X", "Telegram", "Pinterest", "LinkedIn"];

const HEARD_OPTIONS = [
  { value: "google", label: (l) => L("q1_google", l) },
  { value: "instagram", label: (l) => L("q1_instagram", l) },
  { value: "tiktok", label: (l) => L("q1_tiktok", l) },
  { value: "youtube", label: (l) => L("q1_youtube", l) },
  { value: "friend", label: (l) => L("q1_friend", l) },
  { value: "telegram", label: (l) => L("q1_telegram", l) },
  { value: "other", label: (l) => L("q1_other", l) },
];

const AGE_OPTIONS = [
  { value: "under_18", label: (l) => L("q4_u18", l) },
  { value: "18_24", label: (l) => L("q4_18_24", l) },
  { value: "25_34", label: (l) => L("q4_25_34", l) },
  { value: "35_44", label: (l) => L("q4_35_44", l) },
  { value: "45_plus", label: (l) => L("q4_45p", l) },
];

const INTEREST_OPTIONS = [
  { value: "grow_social", label: (l) => L("q5_grow", l) },
  { value: "earn_points", label: (l) => L("q5_earn", l) },
  { value: "business", label: (l) => L("q5_business", l) },
  { value: "curious", label: (l) => L("q5_curious", l) },
];

function ChoiceGrid({ options, value, onChange, lang }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className="rounded-2xl px-3 py-2.5 text-sm font-medium text-left transition-all"
            style={{
              background: selected ? "rgba(64,93,230,0.15)" : "var(--surface-1)",
              border: `2px solid ${selected ? "#405DE6" : "var(--border-color)"}`,
              color: selected ? "#405DE6" : "var(--text-primary)",
            }}
          >
            {opt.label(lang)}
          </button>
        );
      })}
    </div>
  );
}

function MultiGrid({ options, value = [], onChange }) {
  const toggle = (v) => {
    if (value.includes(v)) onChange(value.filter((x) => x !== v));
    else onChange([...value, v]);
  };
  return (
    <div className="grid grid-cols-2 gap-2">
      {options.map((opt) => {
        const selected = value.includes(opt);
        return (
          <button
            key={opt}
            onClick={() => toggle(opt)}
            className="rounded-2xl px-3 py-2.5 text-sm font-medium text-left flex items-center gap-2 transition-all"
            style={{
              background: selected ? "rgba(64,93,230,0.15)" : "var(--surface-1)",
              border: `2px solid ${selected ? "#405DE6" : "var(--border-color)"}`,
              color: selected ? "#405DE6" : "var(--text-primary)",
            }}
          >
            {selected && <Check className="w-3 h-3 flex-shrink-0" />}
            {opt}
          </button>
        );
      })}
    </div>
  );
}

const TOTAL_STEPS = 6;

export default function OnboardingSurvey({ open, onClose }) {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const isRTL = lang === "he";

  const [step, setStep] = useState(1);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  const [heard, setHeard] = useState("");
  const [invitedBy, setInvitedBy] = useState("");
  const [noInvite, setNoInvite] = useState(false);
  const [platforms, setPlatforms] = useState([]);
  const [age, setAge] = useState("");
  const [interest, setInterest] = useState("");
  const [comment, setComment] = useState("");

  const submit = async (status) => {
    if (saving) return;
    setSaving(true);
    try {
      const answers = {};
      if (heard) answers.heard_about_us = heard;
      if (invitedBy.trim()) answers.invited_by = invitedBy.trim();
      if (platforms.length) answers.platforms = platforms;
      if (age) answers.age_category = age;
      if (interest) answers.main_interest = interest;
      if (comment.trim()) answers.optional_comment = comment.trim();

      await fetch(`${API}/onboarding/survey`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status, ...answers }),
      });
    } catch (_) {}
    setSaving(false);
    if (status === "skipped") {
      onClose();
    } else {
      setDone(true);
    }
  };

  const canNext = () => {
    if (step === 1) return true; // heard optional on Q1 too, but let's allow skip
    if (step === 2) return true;
    if (step === 3) return true;
    if (step === 4) return true;
    if (step === 5) return true;
    if (step === 6) return true;
    return true;
  };

  const handleNext = () => {
    if (step < TOTAL_STEPS) setStep(step + 1);
    else submit("completed");
  };

  if (!open) return null;

  return (
    <Dialog open={open}>
      <DialogContent
        hideClose
        className="max-w-md p-0 overflow-hidden"
        style={{ borderRadius: "24px", border: "1px solid var(--border-color)", background: "var(--bg-primary)" }}
        dir={isRTL ? "rtl" : "ltr"}
      >
        {done ? (
          <div className="flex flex-col items-center justify-center gap-4 py-12 px-8 text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center text-3xl"
              style={{ background: "rgba(34,197,94,0.12)" }}>
              ✅
            </div>
            <div className="text-xl font-black">{L("thanks", lang)}</div>
            <div className="text-sm" style={{ color: "var(--text-secondary)" }}>{L("thanks_sub", lang)}</div>
            <button
              onClick={onClose}
              className="mt-2 px-6 py-2.5 rounded-2xl text-sm font-bold text-white transition-all hover:opacity-90"
              style={{ background: "linear-gradient(135deg,#405DE6,#833AB4)" }}
            >
              {lang === "he" ? "מעולה!" : lang === "ru" ? "Отлично!" : "Awesome!"}
            </button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="px-6 pt-6 pb-4" style={{ borderBottom: "1px solid var(--border-color)" }}>
              <div className="flex items-center justify-between mb-1">
                <div className="text-lg font-black">{L("title", lang)}</div>
                <button
                  onClick={() => submit("skipped")}
                  className="text-xs flex items-center gap-1 px-2 py-1 rounded-lg transition-colors hover:bg-white/5"
                  style={{ color: "var(--text-secondary)" }}
                >
                  <SkipForward className="w-3 h-3" />
                  {L("skip_survey", lang)}
                </button>
              </div>
              <div className="text-xs mb-3" style={{ color: "var(--text-secondary)" }}>{L("subtitle", lang)}</div>
              {/* Progress bar */}
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 rounded-full" style={{ background: "var(--border-color)" }}>
                  <div
                    className="h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${(step / TOTAL_STEPS) * 100}%`, background: "linear-gradient(90deg,#405DE6,#833AB4)" }}
                  />
                </div>
                <span className="text-[11px] font-medium flex-shrink-0" style={{ color: "var(--text-secondary)" }}>
                  {L("step_of", lang)} {step} {L("of", lang)} {TOTAL_STEPS}
                </span>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5 min-h-[220px]">
              {step === 1 && (
                <div className="space-y-3">
                  <div className="font-bold text-base">{L("q1_title", lang)}</div>
                  <ChoiceGrid options={HEARD_OPTIONS} value={heard} onChange={setHeard} lang={lang} />
                </div>
              )}

              {step === 2 && (
                <div className="space-y-3">
                  <div className="font-bold text-base">{L("q2_title", lang)}</div>
                  <input
                    className="w-full rounded-2xl px-4 py-2.5 text-sm outline-none"
                    style={{ background: "var(--surface-1)", border: "2px solid var(--border-color)", color: "var(--text-primary)" }}
                    placeholder={L("q2_placeholder", lang)}
                    value={invitedBy}
                    onChange={(e) => { setInvitedBy(e.target.value); if (e.target.value) setNoInvite(false); }}
                    disabled={noInvite}
                  />
                  <button
                    onClick={() => { setNoInvite(!noInvite); if (!noInvite) setInvitedBy(""); }}
                    className="flex items-center gap-2 text-sm"
                    style={{ color: noInvite ? "#405DE6" : "var(--text-secondary)" }}
                  >
                    <div className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0"
                      style={{ border: `2px solid ${noInvite ? "#405DE6" : "var(--border-color)"}`, background: noInvite ? "#405DE6" : "transparent" }}>
                      {noInvite && <Check className="w-2.5 h-2.5 text-white" />}
                    </div>
                    {L("q2_no", lang)}
                  </button>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-3">
                  <div>
                    <div className="font-bold text-base">{L("q3_title", lang)}</div>
                    <div className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>{L("q3_sub", lang)}</div>
                  </div>
                  <MultiGrid options={PLATFORMS} value={platforms} onChange={setPlatforms} />
                </div>
              )}

              {step === 4 && (
                <div className="space-y-3">
                  <div className="font-bold text-base">{L("q4_title", lang)}</div>
                  <ChoiceGrid options={AGE_OPTIONS} value={age} onChange={setAge} lang={lang} />
                </div>
              )}

              {step === 5 && (
                <div className="space-y-3">
                  <div className="font-bold text-base">{L("q5_title", lang)}</div>
                  <ChoiceGrid options={INTEREST_OPTIONS} value={interest} onChange={setInterest} lang={lang} />
                </div>
              )}

              {step === 6 && (
                <div className="space-y-3">
                  <div className="font-bold text-base">{L("q6_title", lang)}</div>
                  <textarea
                    className="w-full rounded-2xl px-4 py-2.5 text-sm outline-none resize-none"
                    style={{ background: "var(--surface-1)", border: "2px solid var(--border-color)", color: "var(--text-primary)", minHeight: "120px" }}
                    placeholder={L("q6_placeholder", lang)}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 pb-6 flex items-center justify-between gap-3" style={{ borderTop: "1px solid var(--border-color)", paddingTop: "16px" }}>
              {step > 1 ? (
                <button
                  onClick={() => setStep(step - 1)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-2xl text-sm font-medium transition-colors hover:bg-white/5"
                  style={{ color: "var(--text-secondary)" }}
                >
                  {isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                  {L("back", lang)}
                </button>
              ) : <div />}
              <button
                onClick={handleNext}
                disabled={saving}
                className="flex items-center gap-1.5 px-5 py-2.5 rounded-2xl text-sm font-bold text-white transition-all hover:opacity-90 disabled:opacity-50"
                style={{ background: "linear-gradient(135deg,#405DE6,#833AB4)" }}
              >
                {step < TOTAL_STEPS ? (
                  <>
                    {L("next", lang)}
                    {isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    {L("finish", lang)}
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
